import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { historyMessage } from "./chatStream";
import { removeLegacyPreferences } from "@/lib/tabPreferences";

export const useChatConversation = (owner, loading) => {
  const [messages, setMessages] = useState([]);
  const runtime = useRef({ generation: 0, session: null, pending: null, controller: null });
  const cancel = useCallback(() => {
    const state = runtime.current;
    state.generation++; state.controller?.abort(); state.controller = null;
    state.session = null; state.pending = null;
  }, []);
  useEffect(() => {
    removeLegacyPreferences(); cancel(); setMessages([]);
    if (loading) return cancel;
    const state = runtime.current, generation = state.generation;
    const controller = new AbortController(); state.controller = controller;
    state.pending = axios.get(`${API}/ai/session/current`, { signal: controller.signal }).then(async ({ data }) => {
      if (!data.session_id || generation !== state.generation) return null;
      const history = await axios.get(`${API}/ai/history/${encodeURIComponent(data.session_id)}`, { signal: controller.signal });
      if (generation !== state.generation) return null;
      state.session = data.session_id; setMessages(history.data.map(historyMessage));
      return data.session_id;
    }).catch(() => null);
    return cancel;
  }, [owner, loading, cancel]);

  const create = useCallback(() => {
    const state = runtime.current, generation = state.generation;
    const controller = new AbortController(); state.controller = controller;
    state.pending = axios.post(`${API}/ai/session`, null, { signal: controller.signal }).then(({ data }) => {
      if (generation !== state.generation) return null;
      state.session = data.session_id; return data.session_id;
    }).catch((err) => { if (generation === state.generation) state.pending = null; throw err; });
    return state.pending;
  }, []);
  const ensureSession = useCallback(async () => {
    const state = runtime.current, generation = state.generation;
    if (state.pending) await state.pending;
    if (generation !== state.generation) return null;
    return state.session || create();
  }, [create]);
  const reset = useCallback(() => {
    cancel(); setMessages([]);
    const generation = runtime.current.generation;
    create().catch((err) => {
      if (generation === runtime.current.generation && !axios.isCancel(err)) setMessages([{ id: crypto.randomUUID(), role: "assistant", text: "Could not start a new conversation. Please try again.", error: true }]);
    });
  }, [cancel, create]);
  return useMemo(() => ({ messages, setMessages, runtime, ensureSession, reset }), [messages, ensureSession, reset]);
};